/**
 * The storage cap's vocabulary — code, copy and error.
 *
 * The GATE lives in `@papan/database`'s `storage-meter.ts`; this file is the
 * shared shape every refusal takes, in the dependency-free package because
 * three layers need it: the API middleware that maps it to a status, the tus
 * server that cannot reach that middleware, and the SPA that branches on the
 * code. Plan: docs/plans — storage cap enforcement, Phase A.
 *
 * Modelled on `write-freeze.ts` beside it, which solves the same problem for
 * the other refusal that can stop a write. Deliberately so: two refusals that
 * a user meets in the same place should not arrive in two different shapes.
 */

/** Machine-readable code on the wire. The SPA branches on this, never on copy. */
export const STORAGE_QUOTA_EXCEEDED_CODE = 'STORAGE_QUOTA_EXCEEDED'

/**
 * A create whose bytes are ALREADY counted, so charging them again would
 * double-count.
 *
 * ── The bug this exists to prevent, found in browser validation ─────────
 *
 * The counted figure is live + bin + versions-excl-current + quarantine. A
 * restore does not add bytes; it moves them between two buckets that both
 * count. But the create chokepoint sees only "a row with `size` bytes" and
 * charges it, while the bin row it came from is still counted — so on a full
 * drive the restore is refused.
 *
 * That turns the cap from a limit into a TRAP: the user can delete a file
 * (allowed, it frees nothing) and then cannot get it back. Delete-then-restore
 * is also the most natural thing to try when a drive is full, so it is the
 * first thing a user would hit.
 *
 * ── Why an explicit flag and not a cleverer chokepoint ──────────────────
 *
 * Nothing at `files.create` can tell "these bytes are new" from "these bytes
 * are moving buckets" — the incoming row looks identical either way, and the
 * row being vacated is not visible from there. Only the caller knows. That is
 * the same reason `WriteFreezeExemption` exists beside it and the shape is
 * deliberately parallel, including a named reason per case rather than a bare
 * boolean, so the justification travels with the call.
 *
 * ── THREE FAMILIES, and the last two are narrow on purpose ─────────────
 *
 * (Conservation, aggregation, operational — with two members in the last.)
 *
 * Most cases below are **conservation** cases: the bytes are already counted
 * somewhere, so charging them would double-count, and the figure is provably
 * unchanged. `storage-conservation.test.ts` is where you prove that.
 *
 * `bulk-charged` is the one **aggregation** case: the bytes ARE charged, by the
 * caller, in one check covering the batch immediately before writing it. It
 * removes a per-row query, not a charge — and it is only sound while the check
 * and the loop sit together, which is why its note insists on that.
 *
 * `workspace-config` and `companion-of-edit` are the **operational** cases —
 * genuinely new bytes, exempted anyway because refusing them takes away
 * something the user already had rather than stopping the drive from growing.
 * Both mirror a `WriteFreezeExemption` of the same name, which is not a
 * coincidence: the freeze reached these two shapes first and for the same
 * reason, so an exemption there is a strong signal one is needed here.
 *
 * They are the whole list, and the bar for a third is the one both cleared:
 * the refusal must cost the user access to what they already own, AND the
 * bytes it lets through must be BOUNDED — a workspace's link list and a
 * `.pmeta`, each one per file with history trimmed like anything else. "It is
 * inconvenient" is not that argument, and neither is "the caller swallows the
 * error" — that is a reason to stop swallowing it.
 *
 * Adding a conservation case means the figure must genuinely be unchanged,
 * which `storage-conservation.test.ts` is where you prove it.
 */
export type StorageCapExemption =
  /**
   * Restoring from the Bin. `bin_items.total_size` already counts these bytes
   * — including the file's superseded version history, folded in by
   * `ba7d1d8b5` — and the bin row is removed by the same operation.
   */
  | { reason: 'bin-restore' }
  /**
   * Restoring from Quarantine. `quarantine_items.size` already counts these
   * bytes and the quarantine row is handed over in the same transaction.
   */
  | { reason: 'quarantine-restore' }
  /**
   * Restoring an older VERSION of a file in place. A conservation case, and
   * the arithmetic is worth stating because it is not obvious:
   *
   * Counted history is `SUM(v.size) WHERE f.content_hash IS DISTINCT FROM
   * v.content_hash` — a version counts only while it is NOT the live content.
   * Rolling back from live `B` to an older `A` therefore swaps which side of
   * that exclusion each one sits on: live goes `B → A` (−B +A), the version
   * row holding `A` stops counting (−A), and the row holding `B` starts (+B).
   * The four terms cancel exactly. Nothing is added and nothing is freed.
   *
   * ── Why it needs the flag rather than falling out of the arithmetic ─────
   *
   * The gate runs after the caller has already re-pointed the row, so the
   * usage it reads is the post-state and the new version matches live ⇒ zero
   * charged. That is correct for a tenant UNDER its ceiling. But a tenant
   * already OVER it — an admin lowered the quota, or history accumulated —
   * fails the `used <= quota` test on an operation that adds nothing, which is
   * precisely when someone needs to roll a file back. So the caller, which is
   * the only place that knows this is a restore, says so.
   *
   * `storage-cap-coverage.test.ts` has always declared `POST /:id/restore`
   * net-neutral; until this the code disagreed with it.
   */
  | { reason: 'version-restore' }
  /**
   * An inbound cross-tenant share LANDING — the `.shared` pointer row, the
   * `Shared`/`_shared` root that holds it, and the `<SourceOrg>` folder
   * beneath. The twin of `WriteFreezeExemption`'s `inbound-share`, and it
   * exists for the same two reasons that argument gives:
   *
   *  1. **It punishes the wrong tenant.** The SHARER performs this action. A
   *     failure caused by the RECIPIENT's drive being full surfaces to someone
   *     who cannot see that drive, cannot free space on it, and did nothing
   *     wrong. Sharing would simply stop working, for a reason nobody could
   *     diagnose from where they were standing.
   *  2. **It buys nothing.** A `.shared` is a cross-tenant REFERENCE — the
   *     content lives in the source tenant and is counted there. What lands in
   *     the recipient is a few hundred bytes of metadata JSON, so charging it
   *     defends the ceiling against a rounding error while breaking a core
   *     flow.
   *
   * NOT for cross-tenant COPY, which really does write the bytes into the
   * recipient and is gated against the DESTINATION like any other write — the
   * same line `WriteFreezeExemption` draws.
   */
  | { reason: 'inbound-share' }
  /**
   * (No production caller since I233 deleted the FileWatcher, its only user — kept for a future recorder of outside
   * edits.) An EXTERNAL edit recorded after the fact — a file
   * changed over SSH, by a script, by a cron job. Papan did not perform this
   * write and learns of it only because the bytes are ALREADY ON DISK.
   *
   * Refusing to record it frees nothing. It leaves the bytes exactly where
   * they are and makes the database disagree with the disk, so the counted
   * figure UNDER-reports — the drive quietly hands out free storage, which is
   * the opposite of what a cap is for, and the gap widens with every
   * subsequent external write.
   *
   * So the honest choice is to record reality and let the cap refuse the next
   * write a user actually makes THROUGH Papan, which it will, because the
   * figure now tells the truth.
   *
   * The trade accepted: someone with write access to the volume can push a
   * tenant over its ceiling. That is not a meaningful bypass — anyone who can
   * write to the storage volume of a self-hosted deployment can already do
   * considerably worse — and it is strictly better than the alternative, which
   * is the same overshoot PLUS a figure that hides it.
   */
  | { reason: 'external-edit' }
  /**
   * A workspace's own `.config` file — the record of which files a workspace
   * links to. The OPERATIONAL exemption described above, and the only one.
   *
   * ── Found in browser validation, and it was breaking the shell ──────────
   *
   * On an over-cap drive `POST /api/workspaces/ensure-system` returned **413**,
   * because `ensureSystemWorkspace` → `addWorkspaceLink` → `writeWorkspaceConfig`
   * writes this file and hit the version chokepoint. So a full drive stopped the
   * user OPENING a workspace over content that was already theirs and already
   * counted — the strict-direction failure this cap is supposed to avoid. A cap
   * exists to stop a drive GROWING with new user content, not to brick
   * navigation over the content already in it.
   *
   * ── Why exempting it is safe ────────────────────────────────────────────
   *
   * The bytes are bounded twice over: the file is a small JSON link list, and
   * its version history is trimmed to `MAX_VERSIONS` by `cleanupOldVersions`
   * like any other file. So the exemption's total exposure is a few kilobytes
   * per workspace — it cannot be driven upward by a caller, which is what
   * separates it from exempting, say, "any write the app makes".
   *
   * ── What it does NOT cover ──────────────────────────────────────────────
   *
   * Only the config file itself. Every file a workspace LINKS TO is ordinary
   * user content on the ordinary gated path; nothing here lets content reach a
   * full drive by being opened in a workspace first.
   */
  | { reason: 'workspace-config' }
  /**
   * These bytes were charged by the caller IN AGGREGATE, immediately before, in
   * one check covering the whole batch. A third family: not conservation and
   * not operational — the same charge, summed.
   *
   * ── Why it has to exist ─────────────────────────────────────────────────
   *
   * `copy()` replays a file's version history one row at a time, inside a
   * `withBlobLock` critical section. Charging each row individually meant a full
   * `driveStorageUsage` — four aggregate subqueries over the tenant's entire
   * `files`/`bin_items`/`file_versions`/`quarantine_items` — plus a `getFile`,
   * PER VERSION. A 5,000-file folder with five versions each is ~25,000
   * full-tenant scans in one long transaction while holding advisory locks on
   * every blob in the subtree. `assertCreateWithinStorageCap` was given a cache
   * for exactly this shape of problem; the version chokepoint had none.
   *
   * ── The rule, which is narrow on purpose ────────────────────────────────
   *
   * The caller MUST have just charged the sum of the bytes it is about to
   * write, against the same tenant, with nothing in between that could change
   * the figure. In practice that means the check and the loop sit in the same
   * function a few lines apart — which is the only arrangement where the two
   * cannot drift. Do NOT reach for this to skip a check that happens
   * "somewhere upstream": a pre-flight in a route or an executor is not this,
   * because work in between can invalidate it.
   */
  | { reason: 'bulk-charged' }
  /**
   * #1424 — the platform's own migration of an app's schema layout rewrites the app's `_appsystem/databases.json` once
   * (each entry gains its `schema`). An OPERATIONAL exemption, like `workspace-config`: a small JSON control file the
   * platform must change on content already counted, once per app per migration — a caller can't drive it upward. Refusing
   * it on an over-cap drive would leave the app on the old layout, which the next release no longer reads.
   */
  | { reason: 'app-layout-migration' }
  /**
   * #1544 — an app's `_appsystem/settings.json` (this copy's values). An OPERATIONAL exemption, NARROW on purpose: only
   * (a) the values sweep — ONE copy of each app's old values, once (the stripped versions stay in counted history until
   * retention: a full drive grows once by at most 512 KB per app, usually a few KB; never user-repeatable), and (b) a
   * save that changes only the `triggers` section (on/off, schedule timing — a few KB, bounded by the app's declared
   * triggers, which are charged) while the whole file stays ≤ `MAX_UNCHARGED_APP_SETTINGS_BYTES` (32 KB — each save is a
   * new version of the WHOLE file), so a person on a full drive can still switch a misfiring trigger off. A variable-value
   * save is CHARGED as before — values are where bulk data could otherwise be stored past the cap.
   */
  | { reason: 'app-settings' }
  /**
   * A `.pmeta` SIDECAR written alongside an edit that already succeeded — the
   * cap's twin of `WriteFreezeExemption`'s `companion-of-edit`, and the second
   * OPERATIONAL exemption.
   *
   * ── Why it must not be charged ──────────────────────────────────────────
   *
   * The sidecar carries editor metadata: column widths, cursor positions, the
   * workbook group's member list. It is written in the same breath as the
   * document body, inside a swallowing catch, and it is not zero-byte — so on a
   * drive at its ceiling the body saved and the metadata silently did not. The
   * user was told the save succeeded and reopened to stale formatting.
   *
   * The reachable case is not even a full drive doing something unusual:
   * content unchanged but metadata changed (someone drags a column) re-points
   * the body to the SAME hash, which costs nothing, and then the sidecar
   * charges its own bytes and throws.
   *
   * Refusing it does not defend the ceiling in any meaningful sense — a
   * `.pmeta` is a few hundred bytes of JSON, one per file, with its history
   * trimmed by `cleanupOldVersions` like anything else — and it costs the user
   * work they cannot see was lost. Same shape as `workspace-config`: the
   * refusal takes away something they already had rather than stopping the
   * drive from growing.
   *
   * ── What it does NOT cover ──────────────────────────────────────────────
   *
   * The document BODY. That is ordinary user content on the ordinary gated
   * path; only the metadata companion rides this.
   */
  | { reason: 'companion-of-edit' }

/**
 * The customer-facing sentence.
 *
 * ── Short on purpose ────────────────────────────────────────────────────
 *
 * An earlier version enumerated the three controls that free counted storage
 * (Bin, old versions, duplicates) and the one thing a member cannot do alone.
 * Accurate, and too long to read: the UploadDock truncated it mid-word, and a
 * toast is glanced at, not studied. It now carries the two things that change
 * what someone does next — the drive is full, and there are two ways out —
 * and leaves the specifics to Settings → Storage, which lists them beside the
 * buttons that perform them.
 *
 * Being told "the drive is full" with NO next step is still the failure this
 * copy exists to avoid; "free up space or ask an admin" is the shortest form
 * that keeps one.
 *
 * **No figures in the prose.** How much is used and how much is allowed are
 * structured fields on the error, present only when the caller is entitled to
 * them (see below). Putting them in the sentence would mean the disclosure
 * decision had to be re-made every time someone edited the wording.
 *
 * English-only, and that is a constraint rather than an oversight — this
 * renders at a SERVER-side refusal, and server-rendered copy in this codebase
 * cannot use i18n (`@papan/i18n` is browser-only). A localized SPA should
 * render its own string from `STORAGE_QUOTA_EXCEEDED_CODE` instead of printing
 * this one. Same stance as `writeFrozenMessage()`.
 */
export function storageQuotaMessage(): string {
  return 'This drive is full. Free up space or ask an admin to raise the limit.'
}

/**
 * Bytes as a short human string, in BINARY units — the vocabulary of the
 * storage cap.
 *
 * Deliberately here and not a sixth private copy. Several byte formatters
 * already exist in this repo (the budget dashboard's, the UploadDock's, the
 * version panel's, the toast's), all of them UI modules a server path cannot
 * import — which is how a refusal message written server-side came to hand-roll
 * its own rounding and claim it matched what Settings → Storage renders, with
 * nothing enforcing that. This package is reachable from a route, a worker and
 * a component, so a figure quoted in a refusal can be produced the same way
 * wherever it is produced.
 *
 * Binary units, one decimal below 10 and whole above — the shape the storage
 * screens already use. The pre-existing UI copies are NOT re-pointed here as
 * part of this: each carries its own rounding contract and its own tests, and
 * changing rendered numbers is not a change to make in passing.
 */
export function formatStorageBytes(n: number): string {
  if (!Number.isFinite(n) || n < 1024) return `${Math.max(0, Math.round(n) || 0)} B`
  const units = ['KB', 'MB', 'GB', 'TB', 'PB']
  let v = n / 1024
  let i = 0
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`
}

/**
 * The sentence for a storage-cap refusal, or `null` if this error is not one.
 *
 * ── Why every full-drive refusal must read the same ─────────────────────
 *
 * A user meets the cap through whichever action they happened to take —
 * upload, paste, extract, saving a document, saving a share to their Drive.
 * Those are separate code paths with separate generic failure strings
 * ("Failed to paste", "Extract failed", …), and answering the SAME condition
 * several different ways makes it look like several different problems. Worse,
 * each generic string drops the only part the user can act on.
 *
 * So the words live once, server-side, in `storageQuotaMessage()`, and every
 * surface renders whatever the server sent. Callers branch on `code`, never on
 * message text — the same seam the write freeze uses.
 *
 * Returns `null` rather than a fallback so each call site keeps its own
 * specific copy for every OTHER failure; this only overrides the storage case.
 *
 * ── Why it lives HERE and not beside its first caller ───────────────────
 *
 * It was exported from `DriveView.tsx`, a ~4,000-line component with the whole
 * React/dnd/query graph behind it. Nothing else could reasonably import that,
 * so the other surfaces hardcoded their own wording — the exact duplication the
 * paragraph above argues against — and its own unit test had to pull the entire
 * component in to exercise six pure branches. This package is dependency-free
 * and already owns the code and the message, so it is importable from a route,
 * a worker and a component alike.
 */
export function storageRefusalText(err: unknown): string | null {
  const e = err as { code?: string; message?: string } | null | undefined
  return e?.code === STORAGE_QUOTA_EXCEEDED_CODE && e.message ? e.message : null
}

/**
 * Thrown by the storage-cap gate when a write would take a tenant over its
 * quota.
 *
 * ── Matched by `name`, so the name is load-bearing ──────────────────────
 *
 * `apps/api/src/middleware/error.ts` maps this to **413** with
 * `STORAGE_QUOTA_EXCEEDED` by checking `err.name`, the pattern already used
 * there for `TenantWriteFrozenError` and `MaxFolderDepthError` — specifically
 * so the middleware need not import the throwing package. Renaming this class
 * silently turns every refused write into an opaque 500.
 *
 * 413 rather than 402: this is not a money problem the customer can settle, it
 * is a request that does not fit. 402 is the budget/dunning branch in the SPA
 * and conflating them would send a full drive to a payment screen.
 *
 * ── Disclosure is the ABSENCE of the figures, not a flag ────────────────
 *
 * A cross-tenant write — uploading into a `.shared` mount, copying into
 * someone else's drive — is gated against the tenant that OWNS the bytes, who
 * is not the caller. That tenant's usage and ceiling are none of the caller's
 * business, and `ba7d1d8b5` already had to strip them from one such refusal.
 *
 * So the thrower simply omits them: `new StorageQuotaExceededError()` for a
 * foreign tenant, `new StorageQuotaExceededError({ usedBytes, quotaBytes })`
 * for the caller's own. A `disclose: false` flag would have worked too, right
 * up until the first consumer forgot to check it — whereas a field that was
 * never attached cannot be leaked by a serializer, a log line or a future
 * error-reporting hook.
 */
export class StorageQuotaExceededError extends Error {
  readonly code = STORAGE_QUOTA_EXCEEDED_CODE
  /** Counted drive storage at the time of refusal. Absent ⇒ foreign tenant. */
  readonly usedBytes?: number
  /** The tenant's ceiling. Absent ⇒ foreign tenant. */
  readonly quotaBytes?: number

  constructor(figures?: { usedBytes: number; quotaBytes: number }) {
    super(storageQuotaMessage())
    // Set explicitly: subclassing Error does not reliably carry the constructor
    // name through transpilation, and the central handler matches on it.
    this.name = 'StorageQuotaExceededError'
    this.usedBytes = figures?.usedBytes
    this.quotaBytes = figures?.quotaBytes
  }
}
